import type { MaskTokens } from '~/modules/mask/runtime/plugin/types'

/**
 * iso код страны в формате [List of ISO 3166 country codes](https://en.wikipedia.org/wiki/List_of_ISO_3166_country_codes)
 **/
export const COUNTRY_CODE = {
  RU: 'RU',
  AM: 'AM',
  BY: 'BY',
  GE: 'GE',
  KG: 'KG',
  KZ: 'KZ',
  MD: 'MD',
  TJ: 'TJ',
  UZ: 'UZ',
} as const

export type CountryCode = InferValues<typeof COUNTRY_CODE>

/**
 * iso код языка в формате [List_of_ISO_639 set-1](https://www.loc.gov/standards/iso639-2/php/code_list.php)
 * @see [Content-Language](https://developer.mozilla.org/ru/docs/Web/HTTP/Headers/Content-Language)
 **/
export const LANGUAGE_CODE = {
  [COUNTRY_CODE.RU]: 'ru',
  [COUNTRY_CODE.AM]: 'hy',
  [COUNTRY_CODE.BY]: 'be',
  [COUNTRY_CODE.GE]: 'ka',
  [COUNTRY_CODE.KG]: 'ky',
  [COUNTRY_CODE.KZ]: 'kk',
  [COUNTRY_CODE.MD]: 'ro',
  [COUNTRY_CODE.TJ]: 'tg',
  [COUNTRY_CODE.UZ]: 'uz',
} satisfies Record<CountryCode, string>

export type LanguageCode = InferValues<typeof LANGUAGE_CODE>

export const LANGUAGE_NAME = {
  [COUNTRY_CODE.RU]: 'Русский',
  [COUNTRY_CODE.AM]: '',
  [COUNTRY_CODE.BY]: 'Беларускі',
  [COUNTRY_CODE.GE]: '',
  [COUNTRY_CODE.KG]: 'Кыргыз тили',
  [COUNTRY_CODE.KZ]: 'Қазақша',
  [COUNTRY_CODE.TJ]: 'Тоҷикӣ',
  [COUNTRY_CODE.MD]: '',
  [COUNTRY_CODE.UZ]: 'O\'zbek',
} satisfies Record<CountryCode, string>

export const COUNTRY_NAME_IN_RU = {
  [COUNTRY_CODE.RU]: 'Россия',
  [COUNTRY_CODE.AM]: '',
  [COUNTRY_CODE.BY]: 'Беларусь',
  [COUNTRY_CODE.GE]: '',
  [COUNTRY_CODE.TJ]: 'Таджикистан',
  [COUNTRY_CODE.KG]: 'Кыргызстан',
  [COUNTRY_CODE.KZ]: 'Казахстан',
  [COUNTRY_CODE.MD]: '',
  [COUNTRY_CODE.UZ]: 'Узбекистан',
} satisfies Record<CountryCode, string>

export const CURRENCY_SYMBOL = {
  [COUNTRY_CODE.RU]: '₽',
  [COUNTRY_CODE.AM]: '',
  [COUNTRY_CODE.BY]: '₽',
  [COUNTRY_CODE.GE]: '',
  [COUNTRY_CODE.KG]: 'с',
  [COUNTRY_CODE.KZ]: '₸',
  [COUNTRY_CODE.MD]: '',
  [COUNTRY_CODE.TJ]: 'с',
  [COUNTRY_CODE.UZ]: 'so\'m',
} satisfies Record<CountryCode, string>

export const PHONE_PREFIX = {
  [COUNTRY_CODE.RU]: '+7',
  [COUNTRY_CODE.AM]: '',
  [COUNTRY_CODE.BY]: '+375',
  [COUNTRY_CODE.GE]: '',
  [COUNTRY_CODE.KG]: '+996',
  [COUNTRY_CODE.KZ]: '+7',
  [COUNTRY_CODE.MD]: '',
  [COUNTRY_CODE.TJ]: '+992',
  [COUNTRY_CODE.UZ]: '+998',
} satisfies Record<CountryCode, string>

export const PHONE_MASK_NUMBER_SYMBOL = 'X'

export const PHONE_MASK = {
  [COUNTRY_CODE.RU]: 'XXX XXX-XX-XX',
  [COUNTRY_CODE.AM]: '',
  [COUNTRY_CODE.BY]: 'XXX XX-XX-XX',
  [COUNTRY_CODE.GE]: '',
  [COUNTRY_CODE.KG]: 'XXX XXX XXX',
  [COUNTRY_CODE.KZ]: 'XXXXX X XX XX',
  [COUNTRY_CODE.MD]: '',
  [COUNTRY_CODE.TJ]: 'XXX XX XXXX',
  [COUNTRY_CODE.UZ]: 'XX XXX XX XX',
} satisfies Record<CountryCode, string>

const NINE_MASK_SYMBOL = '&'

const PHONE_FORMAT_RU = `${PHONE_PREFIX[COUNTRY_CODE.RU]} ${PHONE_MASK[COUNTRY_CODE.RU].replace(PHONE_MASK_NUMBER_SYMBOL, NINE_MASK_SYMBOL)}`

export const PHONE_FORMAT_MASK = {
  [COUNTRY_CODE.RU]: PHONE_FORMAT_RU,
  [COUNTRY_CODE.AM]: `${PHONE_PREFIX[COUNTRY_CODE.AM]} ${PHONE_MASK[COUNTRY_CODE.AM]}`,
  [COUNTRY_CODE.BY]: `${PHONE_PREFIX[COUNTRY_CODE.BY]} ${PHONE_MASK[COUNTRY_CODE.BY]}`,
  [COUNTRY_CODE.GE]: `${PHONE_PREFIX[COUNTRY_CODE.GE]} ${PHONE_MASK[COUNTRY_CODE.GE]}`,
  [COUNTRY_CODE.KG]: `${PHONE_PREFIX[COUNTRY_CODE.KG]} ${PHONE_MASK[COUNTRY_CODE.KG]}`,
  [COUNTRY_CODE.KZ]: `${PHONE_PREFIX[COUNTRY_CODE.KZ]} ${PHONE_MASK[COUNTRY_CODE.KZ]}`,
  [COUNTRY_CODE.TJ]: `${PHONE_PREFIX[COUNTRY_CODE.TJ]} ${PHONE_MASK[COUNTRY_CODE.TJ]}`,
  [COUNTRY_CODE.MD]: `${PHONE_PREFIX[COUNTRY_CODE.MD]} ${PHONE_MASK[COUNTRY_CODE.MD]}`,
  [COUNTRY_CODE.UZ]: `${PHONE_PREFIX[COUNTRY_CODE.UZ]} ${PHONE_MASK[COUNTRY_CODE.UZ]}`,
} satisfies Record<CountryCode, string>

export const PHONE_PLACEHOLDER = {
  [COUNTRY_CODE.RU]: PHONE_FORMAT_MASK[COUNTRY_CODE.RU].replace(NINE_MASK_SYMBOL, '9'),
  [COUNTRY_CODE.AM]: PHONE_FORMAT_MASK[COUNTRY_CODE.AM],
  [COUNTRY_CODE.BY]: PHONE_FORMAT_MASK[COUNTRY_CODE.BY],
  [COUNTRY_CODE.GE]: PHONE_FORMAT_MASK[COUNTRY_CODE.GE],
  [COUNTRY_CODE.KG]: PHONE_FORMAT_MASK[COUNTRY_CODE.KG],
  [COUNTRY_CODE.KZ]: PHONE_FORMAT_MASK[COUNTRY_CODE.KZ],
  [COUNTRY_CODE.MD]: PHONE_FORMAT_MASK[COUNTRY_CODE.MD],
  [COUNTRY_CODE.TJ]: PHONE_FORMAT_MASK[COUNTRY_CODE.TJ],
  [COUNTRY_CODE.UZ]: PHONE_FORMAT_MASK[COUNTRY_CODE.UZ],
} satisfies Record<CountryCode, string>

export const PHONE_MASK_TOKENS: Required<MaskTokens> = {
  [PHONE_MASK_NUMBER_SYMBOL]: { pattern: /\d/ },
  [NINE_MASK_SYMBOL]: { pattern: /9/ },
}

export const ALLOWED_CIS_COUNTRIES_WITHOUT_DOMAIN = [
  // TODO: CIS
  // COUNTRY_CODE.TJ,
  // COUNTRY_CODE.KG,
  // COUNTRY_CODE.UZ
] as const

export const COPYRIGHT_IDENTIFIER = {
  [COUNTRY_CODE.RU]: 'ИНН 2311144947',
  [COUNTRY_CODE.AM]: '',
  [COUNTRY_CODE.BY]: 'УНП 102418021',
  [COUNTRY_CODE.GE]: '',
  [COUNTRY_CODE.KG]: 'ИНН 02703202510097',
  [COUNTRY_CODE.KZ]: 'БИН 250241013133',
  [COUNTRY_CODE.MD]: '',
  [COUNTRY_CODE.TJ]: '',
  [COUNTRY_CODE.UZ]: '',
} satisfies Record<CountryCode, string>

export const PD_URL = {
  [COUNTRY_CODE.RU]: 'https://prodoctorov.ru/',
  [COUNTRY_CODE.AM]: 'https://ydoc.am/',
  [COUNTRY_CODE.BY]: 'https://ydoc.by/',
  [COUNTRY_CODE.GE]: 'https://ydoc.ge/',
  [COUNTRY_CODE.KG]: 'https://ydoc.kg/',
  [COUNTRY_CODE.KZ]: 'https://ydoc.kz/',
  [COUNTRY_CODE.MD]: 'https://ydoc.md/',
  [COUNTRY_CODE.TJ]: 'https://ydoc.tj/',
  [COUNTRY_CODE.UZ]: 'https://ydoc.uz/',
} satisfies Record<CountryCode, string>

export const MF_URL = {
  [COUNTRY_CODE.RU]: 'https://medflex.ru/',
  [COUNTRY_CODE.AM]: '',
  [COUNTRY_CODE.BY]: 'https://medflex.by/',
  [COUNTRY_CODE.GE]: '',
  [COUNTRY_CODE.KG]: 'https://medflex.kg/',
  [COUNTRY_CODE.KZ]: 'https://medflex.kz/',
  [COUNTRY_CODE.MD]: '',
  [COUNTRY_CODE.TJ]: 'https://medflex.tj/',
  [COUNTRY_CODE.UZ]: 'https://medflex.uz/',
} satisfies Record<CountryCode, string>

export const MT_VRACH_URL = {
  [COUNTRY_CODE.RU]: 'https://vrach.medtochka.ru/',
  [COUNTRY_CODE.AM]: 'https://vrach.medtochka.am/',
  [COUNTRY_CODE.BY]: 'https://vrach.medtochka.by/',
  [COUNTRY_CODE.GE]: 'https://vrach.medtochka.ge/',
  [COUNTRY_CODE.KG]: 'https://vrach.medtochka.kg/',
  [COUNTRY_CODE.KZ]: 'https://vrach.medtochka.kz/',
  [COUNTRY_CODE.MD]: 'https://vrach.medtochka.md/',
  [COUNTRY_CODE.TJ]: 'https://vrach.medtochka.tj/',
  [COUNTRY_CODE.UZ]: 'https://vrach.medtochka.uz/',
} satisfies Record<CountryCode, string>

export const LOGO = {
  [COUNTRY_CODE.RU]: '/images/logo/medtochka.svg',
  [COUNTRY_CODE.AM]: '/images/logo/medtochka-am.svg',
  [COUNTRY_CODE.BY]: '/images/logo/medtochka-by.svg',
  [COUNTRY_CODE.GE]: '/images/logo/medtochka-ge.svg',
  [COUNTRY_CODE.KG]: '/images/logo/medtochka-kg.svg',
  [COUNTRY_CODE.KZ]: '/images/logo/medtochka-kz.svg',
  [COUNTRY_CODE.MD]: '/images/logo/medtochka-md.svg',
  [COUNTRY_CODE.TJ]: '/images/logo/medtochka-tj.svg',
  [COUNTRY_CODE.UZ]: '/images/logo/medtochka-uz.svg',
} satisfies Record<CountryCode, string>

export const LOGO_MONO = {
  [COUNTRY_CODE.RU]: '/images/logo/mono/medtochka.svg',
  [COUNTRY_CODE.AM]: '/images/logo/mono/medtochka-am.svg',
  [COUNTRY_CODE.BY]: '/images/logo/mono/medtochka-by.svg',
  [COUNTRY_CODE.GE]: '/images/logo/mono/medtochka-ge.svg',
  [COUNTRY_CODE.KG]: '/images/logo/mono/medtochka-kg.svg',
  [COUNTRY_CODE.KZ]: '/images/logo/mono/medtochka-kz.svg',
  [COUNTRY_CODE.MD]: '/images/logo/mono/medtochka-md.svg',
  [COUNTRY_CODE.TJ]: '/images/logo/mono/medtochka-tj.svg',
  [COUNTRY_CODE.UZ]: '/images/logo/mono/medtochka-uz.svg',
} satisfies Record<CountryCode, string>
